import { dehydrateCanvasData, hydrateCanvasData } from "../storage";
import { generateThumbnail } from "../thumbnail";
import { randomUUID } from "../random";

import type { CanvasData, CanvasMetadata, IStorageAdapter } from "../storage";

const DROPBOX_API_URL = "https://api.dropboxapi.com";
const DROPBOX_CONTENT_URL = "https://content.dropboxapi.com";

interface DropboxAuthConfig {
  appKey: string;
  appSecret: string;
  accessToken?: string;
}

export class DropboxStorageAdapter implements IStorageAdapter {
  private appKey: string;
  private appSecret: string;
  private accessToken: string | null = null;
  private folderPath = "/excalidraw-canvases";

  constructor(config: DropboxAuthConfig) {
    this.appKey = config.appKey;
    this.appSecret = config.appSecret;
    this.accessToken = config.accessToken || null;
  }

  private async ensureAuthenticated(): Promise<void> {
    if (!this.accessToken) {
      throw new Error("Dropbox access token not set. Please authenticate first.");
    }
  }

  private async apiRequest(
    endpoint: string,
    method: string = "POST",
    body?: any,
    isContent: boolean = false,
  ): Promise<any> {
    await this.ensureAuthenticated();

    const baseUrl = isContent ? DROPBOX_CONTENT_URL : DROPBOX_API_URL;
    
    // Use URL parameters to avoid CORS preflight (browser workaround)
    const url = new URL(`${baseUrl}${endpoint}`);
    url.searchParams.set('authorization', `Bearer ${this.accessToken}`);
    
    // Must use exact Content-Type format
    const headers = new Headers();
    let requestBody: string | undefined;
    
    if (isContent) {
      // For content endpoints: use application/octet-stream and arg URL parameter
      headers.set('Content-Type', 'application/octet-stream');
      if (body && body.args) {
        url.searchParams.set('arg', JSON.stringify(body.args));
        requestBody = body.content;
      }
    } else {
      // For RPC endpoints: use dropbox-cors-hack
      headers.set('Content-Type', 'text/plain; charset=dropbox-cors-hack');
      if (body) {
        requestBody = JSON.stringify(body);
      }
    }

    let response: Response;
    try {
      response = await fetch(url.toString(), {
        method,
        headers,
        body: requestBody,
      });
    } catch (fetchError) {
      console.error(`Network error on ${endpoint}:`, fetchError);
      throw new Error(
        `Network error: ${fetchError instanceof Error ? fetchError.message : String(fetchError)}`,
      );
    }

    if (!response.ok) {
      const error = await response.text();
      console.error(`Dropbox API error on ${endpoint}:`, error);
      
      // Parse error message for better user feedback
      try {
        const errorObj = JSON.parse(error);
        const summary = errorObj.error_summary || error;
        throw new Error(`Dropbox API (${response.status}): ${summary}`);
      } catch {
        throw new Error(`Dropbox API error: ${response.status} - ${error}`);
      }
    }

    if (isContent) {
      // For content-download endpoints, result is in Dropbox-API-Result header
      const resultHeader = response.headers.get("Dropbox-API-Result");
      if (resultHeader) {
        // Store metadata for reference if needed
        this.lastContentMetadata = JSON.parse(resultHeader);
      }
      return response.text();
    }

    return response.json();
  }

  private lastContentMetadata: any = null;

  async authenticate(redirectUri: string): Promise<string> {
    if (!redirectUri || redirectUri === 'undefined') {
      redirectUri = window.location.origin;
    }
    const authUrl = `https://www.dropbox.com/oauth2/authorize?client_id=${this.appKey}&response_type=code&redirect_uri=${encodeURIComponent(redirectUri)}`;
    return authUrl;
  }

  async exchangeCodeForToken(code: string, redirectUri: string): Promise<void> {
    const params = new URLSearchParams({
      code,
      grant_type: "authorization_code",
      redirect_uri: redirectUri,
      client_id: this.appKey,
      client_secret: this.appSecret,
    });

    const response = await fetch("https://api.dropbox.com/oauth2/token", {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: params,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to exchange code for access token: ${errorText}`);
    }

    const data = await response.json();
    this.accessToken = data.access_token;
  }

  setAccessToken(token: string): void {
    this.accessToken = token;
  }

  private getMetadataPath(id: string): string {
    return `${this.folderPath}/meta-${id}.json`;
  }

  private getDataPath(id: string): string {
    return `${this.folderPath}/data-${id}.json`;
  }

  async listCanvases(): Promise<CanvasMetadata[]> {
    try {
      // Ensure folder exists first
      try {
        await this.apiRequest("/2/files/create_folder_v2", "POST", {
          path: this.folderPath,
          autorename: false,
        });
      } catch (error: any) {
        // Folder already exists, which is fine
        if (!error.message.includes("conflict")) {
          console.warn("Error creating folder:", error);
        }
      }

      const result = await this.apiRequest("/2/files/list_folder", "POST", {
        path: this.folderPath,
        recursive: false,
      });

      const metadataFiles = result.entries.filter(
        (entry: any) =>
          entry[".tag"] === "file" && entry.name.startsWith("meta-"),
      );

      const metadataPromises = metadataFiles.map(async (file: any) => {
        try {
          const content = await this.apiRequest(
            "/2/files/download",
            "POST",
            { args: { path: file.path_display || file.path_lower } },
            true,
          );
          return JSON.parse(content);
        } catch (err) {
          console.error(`Failed to load metadata file ${file.name}:`, err);
          return null;
        }
      });

      const results = await Promise.all(metadataPromises);
      return results.filter(
        (m: CanvasMetadata | null): m is CanvasMetadata => m !== null,
      );
    } catch (error: any) {
      console.error("List canvases error:", error);
      return [];
    }
  }

  async loadCanvas(id: string): Promise<CanvasData | null> {
    try {
      const content = await this.apiRequest(
        "/2/files/download",
        "POST",
        { args: { path: this.getDataPath(id) } },
        true,
      );
      const rawData = JSON.parse(content);
      return hydrateCanvasData(rawData);
    } catch (error: any) {
      if (error.message.includes("not_found")) {
        return null;
      }
      console.error(`Failed to load canvas ${id}:`, error);
      throw error;
    }
  }

  async saveCanvas(id: string, data: CanvasData): Promise<void> {
    const metadataPath = this.getMetadataPath(id);

    let existingMetadata: CanvasMetadata;
    try {
      const content = await this.apiRequest(
        "/2/files/download",
        "POST",
        { args: { path: metadataPath } },
        true,
      );
      existingMetadata = JSON.parse(content);
    } catch {
      throw new Error("Canvas metadata not found. Cannot save.");
    }

    const thumbnail = await generateThumbnail(
      data.elements,
      data.appState,
      data.files,
    );

    const updatedMetadata: CanvasMetadata = {
      id: existingMetadata.id,
      name: data.appState.name || existingMetadata.name,
      createdAt: existingMetadata.createdAt,
      updatedAt: new Date().toISOString(),
      thumbnail: data.elements.length > 0 ? thumbnail : undefined,
    };

    await this.apiRequest(
      "/2/files/upload",
      "POST",
      {
        args: {
          path: metadataPath,
          mode: {".tag": "overwrite"},
          autorename: false,
          mute: false,
        },
        content: JSON.stringify(updatedMetadata),
      },
      true,
    );

    const dehydratedData = dehydrateCanvasData(data);
    await this.apiRequest(
      "/2/files/upload",
      "POST",
      {
        args: {
          path: this.getDataPath(id),
          mode: {".tag": "overwrite"},
          autorename: false,
          mute: false,
        },
        content: JSON.stringify(dehydratedData),
      },
      true,
    );
  }

  async createCanvas(data: CanvasData): Promise<CanvasMetadata> {
    // Ensure folder exists first
    try {
      await this.apiRequest("/2/files/create_folder_v2", "POST", {
        path: this.folderPath,
        autorename: false,
      });
    } catch (error: any) {
      // Folder already exists, which is fine
      if (!error.message.includes("conflict")) {
        console.warn("Error creating folder:", error);
      }
    }

    const id = randomUUID();
    const now = new Date().toISOString();

    const thumbnail = await generateThumbnail(
      data.elements,
      data.appState,
      data.files,
    );

    const metadata: CanvasMetadata = {
      id,
      name: data.appState.name || "Untitled",
      createdAt: now,
      updatedAt: now,
      thumbnail: data.elements.length > 0 ? thumbnail : undefined,
    };

    await this.apiRequest(
      "/2/files/upload",
      "POST",
      {
        args: {
          path: this.getMetadataPath(id),
          mode: {".tag": "add"},
          autorename: false,
          mute: false,
        },
        content: JSON.stringify(metadata),
      },
      true,
    );

    const dehydratedData = dehydrateCanvasData(data);
    await this.apiRequest(
      "/2/files/upload",
      "POST",
      {
        args: {
          path: this.getDataPath(id),
          mode: {".tag": "add"},
          autorename: false,
          mute: false,
        },
        content: JSON.stringify(dehydratedData),
      },
      true,
    );

    return metadata;
  }

  async deleteCanvas(id: string): Promise<void> {
    try {
      await this.apiRequest("/2/files/delete_v2", "POST", {
        path: this.getMetadataPath(id),
      });
    } catch {}

    try {
      await this.apiRequest("/2/files/delete_v2", "POST", {
        path: this.getDataPath(id),
      });
    } catch {}
  }

  async renameCanvas(id: string, newName: string): Promise<void> {
    const metadataPath = this.getMetadataPath(id);

    const content = await this.apiRequest(
      "/2/files/download",
      "POST",
      { args: { path: metadataPath } },
      true,
    );
    const metadata: CanvasMetadata = JSON.parse(content);

    const updatedMetadata: CanvasMetadata = {
      ...metadata,
      name: newName,
      updatedAt: new Date().toISOString(),
    };

    await this.apiRequest(
      "/2/files/upload",
      "POST",
      {
        args: {
          path: metadataPath,
          mode: {".tag": "overwrite"},
          autorename: false,
          mute: false,
        },
        content: JSON.stringify(updatedMetadata),
      },
      true,
    );
  }
}
